import { DateTime } from 'luxon';
import { useRef, useState } from 'react';

import { cn } from '../cn';
import { Textarea } from '../Input2';
import Button from '../Input2/Button';
import DateBoxButton from '../Input2/DateBoxButton';
import { Arrows } from '../StrokeIcons';

export interface CallOutcomeOption {
  value: string;
  label: string;
}

/**
 * Toast copy shown after a "wrong number" disposition — the server keeps the dialed number but
 * flags it invalid (demoted from primary, never offered again), and may kick off a background
 * search for a replacement. Shared so every surface (dashboard + extension softphone and
 * "Mark as done") gives the rep the same feedback.
 */
export const WRONG_NUMBER_MARKED_TOAST = 'Number marked as wrong — it won’t be dialed again';

/**
 * The call dispositions offered in the UI, shared so every calling surface (in-call softphone, the
 * inline `/todo/calls` dialer, and the call-step "Mark as done" picker) renders the same options.
 * Some produce a side effect server-side: `wrong_number` flags the dialed number invalid (and may
 * trigger a background re-enrichment for a replacement), `do_not_call` flags the number so it's
 * never dialed again (team-local, no replacement search), `firm_line` notes that the number is a
 * firm line, `callback` creates a reminder for the picked date, and `other` appends the rep's
 * typed note (dated) to the contact. The server's `callOutcomeSchema` stays a superset (still
 * accepts the retired `busy`/`not_interested`) so older extension builds sending them keep working.
 *
 * The disposition also decides the contact's caller number from here on: `connected`,
 * `not_interested` and `do_not_call` settle it for good (as does `callback`, when the carrier says
 * the call was answered), while every other outcome advances it to the next number in the rep's
 * bucket — a call that didn't land is treated as evidence that caller ID may be flagged.
 */
export const CALL_OUTCOMES: ReadonlyArray<CallOutcomeOption> = [
  { value: 'voicemail', label: 'Voicemail' },
  // A ring-out with no voicemail is the most common non-connect; without its own button the rep
  // has to reach for "Other" (a two-step note flow) or skip the disposition entirely.
  { value: 'no_answer', label: 'No answer' },
  { value: 'firm_line', label: 'Firm line' },
  { value: 'wrong_number', label: 'Wrong number' },
  { value: 'connected', label: 'Connected' },
  { value: 'callback', label: 'Call back' },
  { value: 'do_not_call', label: 'Do not call' },
  { value: 'other', label: 'Other' },
];

/** Extra data some outcomes carry: a free-text note (`other`) or a call-back date in ms (`callback`). */
export interface CallOutcomeExtra {
  notes?: string;
  callbackAt?: number;
}

export interface CallOutcomePickerProps {
  /** The dispositions to offer. Defaults to {@link CALL_OUTCOMES}. */
  outcomes?: ReadonlyArray<CallOutcomeOption>;
  /** Prompt shown above the grid. */
  title?: string;
  onSelect: (value: string, extra?: CallOutcomeExtra) => void;
  /** When provided, renders a "Skip" affordance below the grid. */
  onSkip?: () => void;
  /** Disable every button while a selection is being saved. */
  disabled?: boolean;
  className?: string;
}

/** Keep ESC inside the note/date inputs from bubbling to a parent that dismisses the dialer. */
const stopEsc = (e: { key: string; stopPropagation: () => void }) => {
  if (e.key === 'Escape') e.stopPropagation();
};

/**
 * Pure "How did it go?" disposition grid. Most outcomes fire immediately on tap; two are two-step:
 * "Other" reveals a note textarea, and "Call back" reveals a date picker. The selection (and any
 * note / call-back date) flows out through `onSelect(value, extra)`; the server turns those into
 * the contact-notes / reminder side effects.
 */
export function CallOutcomePicker({
  outcomes = CALL_OUTCOMES,
  title = 'How did it go?',
  onSelect,
  onSkip,
  disabled,
  className,
}: CallOutcomePickerProps) {
  const [expanded, setExpanded] = useState<null | 'other' | 'callback'>(null);
  const [notes, setNotes] = useState('');
  const [callbackAt, setCallbackAt] = useState<number | null>(null);
  const dateInputRef = useRef<HTMLInputElement>(null);

  const handleGridClick = (value: string) => {
    if (value === 'other') return setExpanded('other');
    if (value === 'callback') return setExpanded('callback');
    onSelect(value);
  };

  const back = () => {
    setExpanded(null);
    setNotes('');
    setCallbackAt(null);
  };

  // "Other" — type a note, then save (empty note logs a plain "Other" with no contact-note append).
  if (expanded === 'other') {
    return (
      <div className={className} onKeyDown={stopEsc}>
        <span className="text-center text-sm text-gray-500 dark:text-gray-400">Add a note</span>
        <Textarea
          className="mt-2"
          tone="outline"
          rows={3}
          autoFocus
          placeholder="What happened on the call?"
          value={notes}
          disabled={disabled}
          onChange={(e) => setNotes(e.target.value)}
        />
        <div className="mt-2 grid grid-cols-2 gap-2">
          <Button size="sm" tone="outline" icon={<Arrows.ArrowLeft />} disabled={disabled} onClick={back}>
            Back
          </Button>
          <Button size="sm" disabled={disabled} onClick={() => onSelect('other', { notes: notes.trim() || undefined })}>
            Save note
          </Button>
        </div>
      </div>
    );
  }

  // "Call back" — pick a date (presets or custom), then save. Save stays disabled until a date is set.
  if (expanded === 'callback') {
    const presets = [
      { label: 'Tomorrow', dt: DateTime.now().plus({ days: 1 }).endOf('day') },
      { label: 'In 3 days', dt: DateTime.now().plus({ days: 3 }).endOf('day') },
      { label: 'Next week', dt: DateTime.now().plus({ weeks: 1 }).endOf('day') },
    ];
    return (
      <div className={className} onKeyDown={stopEsc}>
        <span className="text-center text-sm text-gray-500 dark:text-gray-400">Call back when?</span>
        <div className="mt-2 flex flex-wrap justify-center gap-2" data-test="callback-dates">
          {presets.map((p) => {
            const ms = p.dt.toMillis();
            return (
              <DateBoxButton
                key={p.label}
                size="lg"
                month={p.dt.toFormat('LLL').toUpperCase()}
                day={p.dt.toFormat('d')}
                tooltip={p.label}
                faded={callbackAt != null && callbackAt !== ms}
                disabled={disabled}
                onClick={() => setCallbackAt(ms)}
              />
            );
          })}
          {/* Custom date — native picker behind a date tile. */}
          <DateBoxButton
            size="lg"
            month="•••"
            day={
              callbackAt != null && !presets.some((p) => p.dt.toMillis() === callbackAt)
                ? DateTime.fromMillis(callbackAt).toFormat('d')
                : '?'
            }
            tooltip="Pick a date"
            faded={callbackAt != null && presets.some((p) => p.dt.toMillis() === callbackAt)}
            disabled={disabled}
            onClick={() => dateInputRef.current?.showPicker()}>
            <input
              ref={dateInputRef}
              type="date"
              className="absolute inset-0 cursor-pointer opacity-0"
              min={DateTime.now().plus({ days: 1 }).toISODate() ?? undefined}
              onKeyDown={stopEsc}
              onChange={(e) => {
                const v = e.target.value;
                if (v) setCallbackAt(DateTime.fromISO(v).endOf('day').toMillis());
              }}
            />
          </DateBoxButton>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <Button size="sm" tone="outline" icon={<Arrows.ArrowLeft />} disabled={disabled} onClick={back}>
            Back
          </Button>
          <Button
            size="sm"
            disabled={disabled || callbackAt == null}
            onClick={() => callbackAt != null && onSelect('callback', { callbackAt })}>
            Set reminder
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <span className="text-center text-sm text-gray-500 dark:text-gray-400">{title}</span>
      <div className={cn('mt-2 grid grid-cols-2 gap-2')}>
        {outcomes.map((outcome) => (
          <Button
            key={outcome.value}
            size="sm"
            tone="outline"
            disabled={disabled}
            onClick={() => handleGridClick(outcome.value)}>
            {outcome.label}
          </Button>
        ))}
      </div>
      {onSkip && (
        <Button className="mt-2 w-full" size="sm" text disabled={disabled} onClick={onSkip}>
          Skip
        </Button>
      )}
    </div>
  );
}
